import {
    SpacingToken,
    UiErrorBoundary,
    UiStack,
    UiText,
} from '@experiences/ui-common';
import CloseIcon from '@mui/icons-material/Close';
import type { DrawerProps } from '@mui/material/Drawer';
import Drawer from '@mui/material/Drawer';
import Fade from '@mui/material/Fade';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import type { Theme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import React from 'react';
import { useIntl } from 'react-intl';

import type { Gutter } from '../UiForm';

interface IUiDrawerThemeProps {
    disableGutters?: Gutter[] | boolean;
    width?: 'small' | 'medium' | 'large';
}

const useStyles = makeStyles<Theme, IUiDrawerThemeProps>(theme =>
    createStyles({
        root: {
            height: '100%',
            width: ({ width }: any) => {
                switch (width) {
                    case 'small':
                        return '344px';
                    case 'medium':
                        return '392px';
                    case 'large':
                        return '688px';
                    default:
                        return '344px';
                }
            },
        },
        headerText: {
            fontWeight: 600,
            fontSize: '18px',
            lineHeight: '24px',
            color: theme.palette.semantic.colorForeground,
            wordWrap: 'break-word',
            flex: 1,
            marginRight: '16px',
        },
        iconContainer: {
            marginBottom: '4px',
            alignSelf: 'flex-start',
            flexShrink: 0,
        },
        closeIcon: {
            height: 16,
            width: 16,
        },
        paper: {
            backgroundImage: 'none',
            overflow: 'visible',
        },
    }),
);

interface IErrorObject {
    message: string;
    showError: boolean;
}

const DrawerHeader: React.FC<{
    title?: string;
    themeProps?: IUiDrawerThemeProps;
    customTitle?: React.ReactNode;
    onClose: () => void;
}> = ({
    title,
    customTitle,
    themeProps,
    onClose,
}) => {
    const classes = useStyles({ ...themeProps });
    const { formatMessage: translate } = useIntl();

    return (
        <UiStack
            direction="row"
            justify="between"
            align="center"
            pt={10}
            pr={12}
            pl={24}
            style={{ flexShrink: 0 }}>
            {!customTitle && (
                <UiText
                    className={classes.headerText}
                    data-cy="ui-drawer-title"
                    role="heading"
                    aria-level={1}
                >
                    {title}
                </UiText>
            )}
            {customTitle}
            <IconButton
                onClick={onClose}
                className={classes.iconContainer}
                aria-label={translate({ id: 'CLIENT_CLOSE' })}
                data-cy="close-button"
            >
                <CloseIcon className={classes.closeIcon} />
            </IconButton>
        </UiStack>
    );
};

export const UiDrawer: React.FC<{
    title?: string;
    customTitle?: React.ReactNode;
    children?: React.ReactNode;
    drawerProps?: DrawerProps;
    themeProps?: Omit<IUiDrawerThemeProps, 'width'>;
    width?: 'small' | 'medium' | 'large';
    loading?: boolean;
    error?: IErrorObject;
}> = ({
    title,
    customTitle,
    children,
    drawerProps,
    themeProps,
    width = 'small',
    loading,
    error,
}) => {
    const classes = useStyles({
        ...themeProps,
        disableGutters: themeProps?.disableGutters,
        width,
    });
    const { formatMessage: translate } = useIntl();

    const handleClose = () => {
        drawerProps?.onClose && drawerProps.onClose({}, 'escapeKeyDown');
    };

    // Determine effectiveDisableGutters based on prop type
    let effectiveDisableGutters: Gutter[] = [];
    if (themeProps?.disableGutters === true) {
        effectiveDisableGutters = [ 'top', 'right', 'bottom', 'left' ];
    } else if (Array.isArray(themeProps?.disableGutters)) {
        effectiveDisableGutters = themeProps.disableGutters;
    }

    return (
        <Drawer
            {...drawerProps}
            classes={{ paper: classes.paper }}
            style={{ whiteSpace: 'pre-wrap' }}
            role="main"
        >
            <UiStack
                className={classes.root}
                direction="column"
                role="dialog"
                aria-label={translate({ id: 'CLIENT_DRAWER' }, { 0: title })}
                style={{ height: '100%' }}
            >
                <DrawerHeader
                    title={title}
                    customTitle={customTitle}
                    themeProps={themeProps}
                    onClose={handleClose}
                />
                {loading && (
                    <LinearProgress
                        sx={{
                            height: 3,
                            borderRadius: 2,
                            mb: 1,
                            mx: 1.5,
                        }}
                        color="primary"
                    />
                )}
                <UiErrorBoundary>
                    {error?.showError ? (
                        <UiStack
                            direction="column"
                            flex={1}
                            p={SpacingToken.L}
                            data-cy="drawer-error">
                            <UiText>
                                {error?.message || 'Error'}
                            </UiText>
                        </UiStack>
                    ) : (
                        <Fade
                            in={!loading}
                            timeout={400}
                            unmountOnExit>
                            <div style={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                flex: 1,
                                minHeight: 0,
                            }}>
                                <UiStack
                                    direction="column"
                                    flex={1}
                                    style={{
                                        overflowY: 'auto',
                                        overflowX: 'hidden',
                                        minHeight: 0,
                                        width: '100%',
                                        boxSizing: 'border-box',
                                    }}
                                    pl={effectiveDisableGutters.includes('left') ? 0 : SpacingToken.L}
                                    pr={effectiveDisableGutters.includes('right') ? 0 : SpacingToken.L}
                                    pt={effectiveDisableGutters.includes('top') ? 0 : SpacingToken.L}
                                    pb={effectiveDisableGutters.includes('bottom') ? 0 : SpacingToken.L}
                                >
                                    <UiStack
                                        direction="column"
                                        flex={1}
                                        style={{ overflow: 'auto' }}
                                    >
                                        {children}
                                    </UiStack>
                                </UiStack>
                            </div>
                        </Fade>
                    )}
                </UiErrorBoundary>
            </UiStack>
        </Drawer>
    );
};
